import { INTAKE_ROOT_PATH, isPayerConnectionsPath, resolveIntakePageKey } from '../../routing';

import type { IntakeJourneyStage } from './IntakeJourneyShell';
import {
  effectiveIntakeStatusTab,
  INTAKE_STATUS_TABS,
  type IntakeStatusTab,
} from './IntakeStatusDashboard';

export function resolveJourneyStage(
  pathname: string,
  search: string,
  payerEnabled: boolean,
  defaultStage: 'agreements' | 'intake' = 'agreements',
  /** The billing stage exists only for gated practice admins; others fall through. */
  billingAvailable = false,
): IntakeJourneyStage {
  const params = new URLSearchParams(search);
  const stage = params.get('intakeStage');
  if (stage === 'billing' && billingAvailable) return 'billing';
  if (
    stage === 'agreements' ||
    stage === 'intake' ||
    stage === 'verification' ||
    stage === 'go-live'
  ) {
    return stage;
  }
  if (stage === 'import') return 'go-live';
  if (stage === 'payers') return payerEnabled ? 'go-live' : 'intake';
  if (isPayerConnectionsPath(pathname) || params.get('intakeSection') === 'payer-connections') {
    return payerEnabled ? 'go-live' : 'intake';
  }
  return resolveIntakePageKey(pathname) || params.has('intakeStep') ? 'intake' : defaultStage;
}

/** Keep the nested intake step and unrelated query parameters through every stage roundtrip. */
export function journeyStageLocation(pathname: string, search: string, stage: IntakeJourneyStage) {
  const params = new URLSearchParams(search);
  params.set('intakeStage', stage);
  params.delete('intakeSection');
  return {
    pathname: isPayerConnectionsPath(pathname) ? INTAKE_ROOT_PATH : pathname,
    search: params.toString(),
  };
}

/** Links to a track the practice did not elect fall back to its first available track. */
export function resolveIntakeStatusTab(
  search: string,
  available: readonly IntakeStatusTab[] = INTAKE_STATUS_TABS,
): IntakeStatusTab {
  const params = new URLSearchParams(search);
  const tab = params.get('intakeStatus');
  const requested: IntakeStatusTab =
    tab === 'credentialing' || tab === 'ehr-import' || tab === 'billing'
      ? tab
      : params.get('intakeStage') === 'import'
        ? 'ehr-import'
        : 'billing';
  return effectiveIntakeStatusTab(requested, available);
}

export function intakeStatusLocation(pathname: string, search: string, tab: IntakeStatusTab) {
  const next = journeyStageLocation(pathname, search, 'go-live');
  const params = new URLSearchParams(next.search);
  params.set('intakeStatus', tab);
  return { ...next, search: params.toString() };
}
